import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import { useSdk, translate } from '@fondationcss/sdk';
import { useSnackbar } from '@fondationcss/ui';
import { getCustomSdk } from '@/sdk';
import type { Site } from '@/sdk/model/site';
import type { LearningProgram } from '@/sdk/model/learning-program';
import { GroupStatus, type Group } from '@/sdk/model/group';
import type { Teacher } from '@/sdk/model/teacher';
import type { PresenceTypeDefinition } from '@/sdk/model/presence-type-definition';
import type { ContextDropdown } from '@/routes';
import { type StudentFilterCriteria, emptyStudentFilter, normalizeStudentFilter } from '@/utils/student-filter';
import { PROGRAM_SCOPE_ALL, isProgramScope, withProgramScopeEntries } from '@/utils/program-scope';
import { formatUserFullName } from '@/utils/user-utils';
import { isExamSupervisorOnly } from '@/utils/exam-supervision-feature';

const STORAGE_KEY = 'ctx-toolbar-selection';

const PERMISSION_ENSEIGNANT = 'Parcours.LearningModule.Enseignant';
const PERMISSION_SUPERVISEUR = 'Parcours.LearningModule.Superviseur';
const PERMISSION_ADMINISTRATEUR = 'Parcours.LearningModule.Administrateur';
/** « Enseignement élargi », held by the « Enseignant chef » role (#879, phase 1; bounded by #1083). */
const PERMISSION_ENSEIGNEMENT_ELARGI = 'Parcours.LearningModule.EnseignementÉlargi';
/**
 * The permissions whose TeacherScope does not pin the teacher to themselves on the server
 * (`TeacherScope.LocksTeacherToSelf` false): staff, who read everything, and the chef, who reads
 * every student of their assigned programs (#1083).
 */
const UNRESTRICTED_SCOPE_PERMISSIONS = [PERMISSION_ADMINISTRATEUR, PERMISSION_SUPERVISEUR, PERMISSION_ENSEIGNEMENT_ELARGI];

type HighlightRouteName = string | symbol | null;

interface SavedToolbarState {
    siteId?: string | null;
    programId?: string | null;
    confirmedGroupId?: string | null;
    unconfirmedGroupId?: string | null;
    allGroupId?: string | null;
    groupId?: string | null; // legacy — migrated on first restore
    teacherId?: string | null;
    pinnedSite?: boolean;
    pinnedProgram?: boolean;
    pinnedGroup?: boolean;
    pinnedTeacher?: boolean;
    studentFilter?: StudentFilterCriteria | null;
    activityTypes?: string[] | null;
}

export const useContextToolbarStore = defineStore('contextToolbar', () => {
    const sdk = getCustomSdk();
    const coreSdk = useSdk();
    const { showWarning } = useSnackbar();

    /**
     * True when the server does not pin the signed-in user to their own point of view: an
     * Administrateur, a Superviseur, or an « Enseignant chef » holding « Enseignement élargi »
     * (#879). Mirrors `TeacherScope.LocksTeacherToSelf` (TeacherScopeService). Everything the
     * toolbar offers beyond the user's own students only means something to them.
     *
     * « Unrestricted » is the filter, not the rows since #1083: a chef picks any teacher and the
     * pseudo-programs, and the server answers with the students of the chef's assigned programs
     * only — as it cuts every other reading down to the session's rows.
     */
    const unrestrictedTeacherScope = computed(() => {
        const permissions = coreSdk.me.value?.permissions ?? [];
        return UNRESTRICTED_SCOPE_PERMISSIONS.some(p => permissions.includes(p));
    });

    /**
     * True for the « Surveillant d'examen » (#1164): the exam supervision is the account's only
     * way to record presences. The bar has nothing to offer it — sites, programs, groups,
     * teachers and the presence type catalogue all answer it 403 — so it draws no axis and loads
     * nothing (see `visibleContextAxes`). Permission only, flag or not: with the flag off those
     * reads are refused all the same. False while the signed-in user is not known.
     */
    const examSupervisorOnly = computed(() => isExamSupervisorOnly(coreSdk.me.value?.permissions));

    // ─── Data lists ───────────────────────────────────────────────────────────
    const sites = ref<Site[]>([]);
    const programs = ref<LearningProgram[]>([]);
    const groups = ref<Group[]>([]);
    const teachers = ref<Teacher[]>([]);

    // ─── Loading states ────────────────────────────────────────────────────────
    const loadingSites = ref(false);
    const loadingPrograms = ref(false);
    const loadingGroups = ref(false);
    const loadingTeachers = ref(false);

    // ─── Selected values ───────────────────────────────────────────────────────
    const selectedSite = ref<Site | null>(null);
    const selectedProgram = ref<LearningProgram | null>(null);
    const selectedGroupConfirmed = ref<Group | null>(null);
    const selectedGroupUnconfirmed = ref<Group | null>(null);
    const selectedGroupAll = ref<Group | null>(null);
    const selectedTeacher = ref<Teacher | null>(null);

    // ─── Pin states (dropdown → label) ────────────────────────────────────────
    const pinnedSite = ref(false);
    const pinnedProgram = ref(false);
    const pinnedGroup = ref(false);
    const pinnedTeacher = ref(false);

    /** Whether the initial data load has completed at least once. */
    const initialized = ref(false);

    /** Dropdowns currently pulsing to attract user attention. Set by individual pages. */
    const highlight = ref<ContextDropdown[]>([]);
    const highlightRoute = ref<HighlightRouteName>(null);
    const highlightOwner = ref<symbol | null>(null);

    /**
     * Axes a screen narrows itself to, beyond what its route declares (#924). A route says which
     * dropdowns its page could ever need; a page that knows more — the Rapports pages, where the
     * chosen report decides what is read — says which of them it needs right now. Null means the
     * route's own list stands, which is every screen but those.
     *
     * Owned like the highlight, and for the same reason: two screens must not fight over it, and
     * the one that set it is the one that clears it.
     */
    const axisRestriction = ref<ContextDropdown[] | null>(null);
    const axisRestrictionRoute = ref<HighlightRouteName>(null);
    const axisRestrictionOwner = ref<symbol | null>(null);

    /** Status filter for the group dropdown. Drives which slot selectedGroup reads/writes. */
    const groupStatusFilter = ref<GroupStatus | 'all'>(GroupStatus.Confirmed);

    /** Shared multi-criteria student filter, edited via the toolbar's StudentFilter control. */
    const studentFilter = ref<StudentFilterCriteria>(emptyStudentFilter());

    /**
     * The « Activité » filter of the Présences sheet (#1163): presence type keys of the catalogue
     * (`PresenceTypeDefinition.key`), never an enum — a later issue locks an exam supervisor to
     * the exam types, and the catalogue is what both sides read. Outside the cascade: the
     * program and the teacher say WHOSE sheet it is, the activity says WHICH cells of it, and
     * neither clears the other. Empty = no filter.
     */
    const selectedActivityTypes = ref<string[]>([]);
    /** The catalogue behind the filter, in its `order`. Filled by `loadActivityTypes()` alone. */
    const activityTypes = ref<PresenceTypeDefinition[]>([]);
    const loadingActivityTypes = ref(false);
    let activityTypesLoad: Promise<void> | null = null;

    /** Trimmed, blanks dropped, distinct — what the server does with the query, done once here. */
    function normalizeActivityTypes(keys: unknown): string[] {
        if (!Array.isArray(keys)) return [];
        return [...new Set(keys
            .filter((key): key is string => typeof key === 'string')
            .map(key => key.trim())
            .filter(key => key.length > 0))];
    }

    function setActivityTypes(keys: string[]) {
        selectedActivityTypes.value = normalizeActivityTypes(keys);
    }

    /**
     * What the filter reads as — on the toolbar's trigger, and in the figures it narrows. Null
     * with no type kept, the catalogue label of a single type, a count for several. The
     * catalogue may not be there yet, or at all when its load failed: a count then, never the
     * raw key, which is an English identifier.
     */
    const selectedActivityLabel = computed<string | null>(() => {
        const keys = selectedActivityTypes.value;
        if (keys.length === 0) return null;
        if (keys.length === 1) return activityTypes.value.find(t => t.key === keys[0])?.label ?? translate('parcours.toolbar.activity.singular');
        return translate('parcours.toolbar.activity.plural', { count: keys.length });
    });

    /**
     * Drops the keys the catalogue no longer has, once it is known. A type deleted from the
     * catalogue would otherwise keep filtering the sheet down to nothing, with no entry in the
     * menu to say so.
     */
    function pruneActivityTypesToCatalogue() {
        if (!activityTypes.value.length) return;
        const known = new Set(activityTypes.value.map(t => t.key));
        const kept = selectedActivityTypes.value.filter(key => known.has(key));
        if (kept.length !== selectedActivityTypes.value.length) selectedActivityTypes.value = kept;
    }

    /**
     * Loads the catalogue once, on the first route that shows the axis — NOT in `initialize()`:
     * every page mounts the bar, and only the Présences sheet reads presence types. A failed
     * load is retried on the next call, the selection left as it is: the server ignores a key
     * it does not know.
     */
    function loadActivityTypes(): Promise<void> {
        // The bar never shows the axis to the exam supervisor, whose sheet is locked on the exam
        // types without the catalogue (#1164); this is the net under that, as the route answers
        // them 403.
        if (examSupervisorOnly.value) return Promise.resolve();
        if (activityTypesLoad) return activityTypesLoad;
        activityTypesLoad = (async () => {
            loadingActivityTypes.value = true;
            try {
                const res = await sdk.getPresenceTypeDefinitions();
                activityTypes.value = [...(res?.definitions ?? [])].sort((a, b) => a.order - b.order);
                pruneActivityTypesToCatalogue();
            } catch (e) {
                activityTypesLoad = null;
                console.error('Failed to load presence types', e);
            } finally {
                loadingActivityTypes.value = false;
            }
        })();
        return activityTypesLoad;
    }

    /** Active group selection — reads/writes the slot matching the current groupStatusFilter. */
    const selectedGroup = computed({
        get: () => {
            if (groupStatusFilter.value === 'all') return selectedGroupAll.value;
            return groupStatusFilter.value === GroupStatus.Confirmed
                ? selectedGroupConfirmed.value
                : selectedGroupUnconfirmed.value;
        },
        set: (val: Group | null) => {
            if (groupStatusFilter.value === 'all') {
                selectedGroupAll.value = val;
            } else if (groupStatusFilter.value === GroupStatus.Confirmed) {
                selectedGroupConfirmed.value = val;
            } else {
                selectedGroupUnconfirmed.value = val;
            }
        },
    });

    /** Flag set during localStorage restore to suppress cascade resets. */
    let isRestoring = false;

    // ─── Computed filtered lists ────────────────────────────────────────────────

    /** Programs filtered by the selected site (or all loaded programs if a site is selected). */
    const filteredPrograms = computed<LearningProgram[]>(() => {
        if (!selectedSite.value) return programs.value;
        // The pseudo-programs (#815/#948) carry no site of their own and stay offered
        // everywhere: a family of programs exists wherever its members do.
        return programs.value.filter(p =>
            isProgramScope(p) || p.sites.some(s => s.id === selectedSite.value!.id)
        );
    });

    /** Non-archived groups filtered by the selected program and status. */
    const filteredGroups = computed<Group[]>(() => {
        const active = groups.value.filter(g => !g.archived);
        const statusFiltered = groupStatusFilter.value === 'all'
            ? active
            : active.filter(g => g.status === groupStatusFilter.value);
        if (!selectedProgram.value) return statusFiltered;
        return statusFiltered.filter(g => g.learningProgramId === selectedProgram.value!.id);
    });

    /** Teachers filtered by the selected group / program. Populated via loadTeachers(). */
    const filteredTeachers = computed<Teacher[]>(() => teachers.value);

    // ─── Data loading ──────────────────────────────────────────────────────────

    async function loadSites() {
        loadingSites.value = true;
        try {
            const res = await sdk.getSites();
            sites.value = (res?.sites ?? []).filter(s => !s.archived);
        } catch (e) {
            console.error('Failed to load sites', e);
        } finally {
            loadingSites.value = false;
        }
    }

    async function loadPrograms() {
        loadingPrograms.value = true;
        try {
            const res = selectedSite.value
                ? await sdk.learningPrograms()
                : await sdk.availableLearningPrograms();
            programs.value = offerProgramScopes(res?.programs ?? []);
        } catch (e) {
            console.error('Failed to load programs', e);
        } finally {
            loadingPrograms.value = false;
        }
    }

    /**
     * The pseudo-entries (#815, widened to three by #948) lead the menu whenever there is a real
     * program to unite — for staff and the chef only. Decided with Frederick on 2026-09-09
     * (« l'option … devrait apparaître seulement pour les enseignants chefs » — « exact ! »): a
     * scoped teacher only ever sees their own students, so a union has nothing to offer them
     * and is not listed. For a chef, « Individualisés » unites the individualised students of
     * their assigned programs (#1083) — the server applies that bound.
     */
    function offerProgramScopes(list: LearningProgram[]): LearningProgram[] {
        return unrestrictedTeacherScope.value
            ? withProgramScopeEntries(list)
            : list.filter(p => !isProgramScope(p));
    }

    async function loadGroups() {
        loadingGroups.value = true;
        try {
            const res = await sdk.groups();
            groups.value = res?.groups ?? [];
        } catch (e) {
            console.error('Failed to load groups', e);
        } finally {
            loadingGroups.value = false;
        }
    }

    // ─── Lookup helpers (token → entity, case-insensitive) ────────────────────

    function eq(a: string | null | undefined, b: string): boolean {
        return !!a && a.toLowerCase() === b.toLowerCase();
    }

    function findByToken<T>(collection: T[], token: string, predicates: Array<(item: T) => boolean>): T | null {
        if (!token) return null;
        for (const predicate of predicates) {
            const found = collection.find(predicate);
            if (found) return found;
        }
        return null;
    }

    /** id → code → name */
    function findSite(token: string): Site | null {
        return findByToken(sites.value, token, [
            s => s.id === token,
            s => eq(s.code, token),
            s => eq(s.name, token),
        ]);
    }

    /** id → code → title */
    function findProgram(token: string): LearningProgram | null {
        return findByToken(programs.value, token, [
            p => p.id === token,
            p => eq(p.code, token),
            p => eq(p.title, token),
        ]);
    }

    /** id → name */
    function findGroup(token: string): Group | null {
        return findByToken(groups.value, token, [
            g => g.id === token,
            g => eq(g.name, token),
        ]);
    }

    /** id → "first last" → email */
    function findTeacher(token: string): Teacher | null {
        return findByToken(teachers.value, token, [
            t => t.id === token,
            t => eq(formatUserFullName(t.user), token),
            t => eq(t.user.email, token),
        ]);
    }

    /** Tracks the latest loadTeachers call to discard stale responses. */
    let teacherLoadSeq = 0;

    /**
     * Loads teachers scoped to the current selection:
     * - selected group or program → all non-archived teachers for that program
     * - nothing selected → all non-archived teachers
     *
     * VACANCIES are excluded (`isVacancy`). Found in acceptance testing on 28/08: « Poste
     * solveur 2 À pourvoir » and « test À pourvoir » appeared at the top of the Enseignant
     * menu, before the real people. A vacancy is a hiring need, not someone whose schedule or
     * presences can be displayed — choosing it frames nothing. It obviously stays visible
     * where it is the subject, the Embauches page.
     */
    async function loadTeachers(): Promise<boolean> {
        const seq = ++teacherLoadSeq;
        loadingTeachers.value = true;
        try {
            let result: Teacher[];

            const selectable = (t: Teacher) => !t.archived && !t.isVacancy;
            // A retained pinned group can temporarily disagree with a newly selected program.
            // The explicit program is the active scope in that case; the group is only the
            // fallback when a page selects a group without selecting its program first.
            const programId = selectedProgram.value?.id ?? selectedGroup.value?.learningProgramId;
            if (programId) {
                const res = await sdk.teachersByProgram(programId);
                result = (res?.teachers ?? []).filter(selectable);
            } else {
                const res = await sdk.teachers();
                result = (res?.teachers ?? []).filter(selectable);
            }

            // Discard stale responses caused by rapid selection changes.
            if (seq === teacherLoadSeq) {
                teachers.value = result;
                return true;
            }
        } catch (e) {
            if (seq === teacherLoadSeq) {
                console.error('Failed to load teachers', e);
            }
        } finally {
            if (seq === teacherLoadSeq) {
                loadingTeachers.value = false;
            }
        }
        return false;
    }

    /**
     * Where the pinned teacher teaches: group ids with planned modules, and the programs
     * they are associated with. Both sets are empty whenever no teacher is pinned.
     */
    const pinnedTeacherGroupIds = ref<ReadonlySet<string>>(new Set());
    const pinnedTeacherProgramIds = ref<ReadonlySet<string>>(new Set());

    /** Tracks the latest planning fetch to discard stale responses (same idea as teacherLoadSeq). */
    let pinnedTeacherGroupsSeq = 0;

    watch([pinnedTeacher, selectedTeacher], async ([isPinned, teacher]) => {
        const seq = ++pinnedTeacherGroupsSeq;
        if (!isPinned || !teacher) {
            pinnedTeacherGroupIds.value = new Set();
            pinnedTeacherProgramIds.value = new Set();
            return;
        }
        try {
            const res = await sdk.getTeacherPlanning(teacher.id);
            if (seq === pinnedTeacherGroupsSeq) {
                pinnedTeacherGroupIds.value = new Set((res?.groups ?? []).map(g => g.groupId));
                pinnedTeacherProgramIds.value = new Set((res?.programs ?? []).map(p => p.learningProgramId));
            }
        } catch (e) {
            // The constraints are a convenience: on failure apply none rather than block anything.
            if (seq === pinnedTeacherGroupsSeq) {
                pinnedTeacherGroupIds.value = new Set();
                pinnedTeacherProgramIds.value = new Set();
                console.error('Failed to load the pinned teacher\'s groups', e);
            }
        }
    });

    /**
     * Teacher ids actually assigned in the pinned group's planning. Empty when no group is
     * pinned — or when the group has no assignments yet, in which case no constraint applies
     * (an in-preparation group must not turn the whole teacher menu off).
     */
    const pinnedGroupTeacherIds = ref<ReadonlySet<string>>(new Set());

    let pinnedGroupTeachersSeq = 0;

    watch([pinnedGroup, selectedGroup], async ([isPinned, group]) => {
        const seq = ++pinnedGroupTeachersSeq;
        if (!isPinned || !group) {
            pinnedGroupTeacherIds.value = new Set();
            return;
        }
        try {
            const res = await sdk.getGroupPlanning(group.id);
            if (seq === pinnedGroupTeachersSeq) {
                pinnedGroupTeacherIds.value = new Set(
                    (res?.groupSchedule?.modules ?? [])
                        .map(m => m.assignedTeacherId)
                        .filter((id): id is string => !!id)
                );
            }
        } catch (e) {
            if (seq === pinnedGroupTeachersSeq) {
                pinnedGroupTeacherIds.value = new Set();
                console.error('Failed to load the pinned group\'s teachers', e);
            }
        }
    });

    // ─── Pin constraints (#838): a pinned axis narrows the OTHER menus ─────────
    //
    // The left-to-right cascade already filters downstream lists. These sets are the
    // right-to-left counterpart: with an axis pinned, the other menus keep showing every
    // option but disable the ones incompatible with the pinned value (and mark the rest).
    // `null` = no active constraint on that menu. An EMPTY source set contributes no
    // constraint on purpose: it means "still loading" or "relation unknown/unplanned",
    // and disabling an entire menu on missing data would be worse than not helping.

    function intersectionOf(sets: Array<ReadonlySet<string>>): ReadonlySet<string> | null {
        const active = sets.filter(s => s.size > 0);
        if (!active.length) return null;
        return active.reduce((acc, s) => new Set([...acc].filter(id => s.has(id))));
    }

    /** Sites offering at least one of the given programs (looked up in the loaded list). */
    function sitesOfPrograms(programIds: ReadonlySet<string>): Set<string> {
        return new Set(
            programs.value
                .filter(p => programIds.has(p.id))
                .flatMap(p => p.sites.map(s => s.id))
        );
    }

    const enabledSiteIds = computed<ReadonlySet<string> | null>(() => {
        const sets: Array<ReadonlySet<string>> = [];
        if (pinnedProgram.value && selectedProgram.value) {
            sets.push(new Set(selectedProgram.value.sites.map(s => s.id)));
        }
        if (pinnedGroup.value && selectedGroup.value) {
            sets.push(new Set([selectedGroup.value.siteId]));
        }
        if (pinnedTeacher.value && selectedTeacher.value) {
            sets.push(sitesOfPrograms(pinnedTeacherProgramIds.value));
        }
        return intersectionOf(sets);
    });

    const enabledProgramIds = computed<ReadonlySet<string> | null>(() => {
        // A pinned site needs no set here: filteredPrograms already hides its incompatibles.
        const sets: Array<ReadonlySet<string>> = [];
        if (pinnedGroup.value && selectedGroup.value) {
            sets.push(new Set([selectedGroup.value.learningProgramId]));
        }
        if (pinnedTeacher.value && selectedTeacher.value) {
            sets.push(pinnedTeacherProgramIds.value);
        }
        const constrained = intersectionOf(sets);
        if (!constrained) return null;
        // The constraint sources only know real program ids. A pseudo-entry (#815/#948) stays
        // offered as long as one program of its family survives the constraint — a union cannot
        // be narrower than one of its members.
        const survives = (family: boolean) => programs.value.some(p =>
            !isProgramScope(p) && !!p.isIndividual === family && constrained.has(p.id));
        const individualSurvives = survives(true);
        const groupSurvives = survives(false);
        const enabledScopes = programs.value
            .filter(p => isProgramScope(p))
            .filter(p => (p.id === PROGRAM_SCOPE_ALL
                ? individualSurvives || groupSurvives
                : p.isIndividual ? individualSurvives : groupSurvives))
            .map(p => p.id);
        return enabledScopes.length ? new Set([...constrained, ...enabledScopes]) : constrained;
    });

    const enabledGroupIds = computed<ReadonlySet<string> | null>(() => {
        // A pinned program needs no set here: filteredGroups already hides its incompatibles.
        const sets: Array<ReadonlySet<string>> = [];
        if (pinnedSite.value && selectedSite.value) {
            sets.push(new Set(
                groups.value.filter(g => g.siteId === selectedSite.value!.id).map(g => g.id)
            ));
        }
        if (pinnedTeacher.value && selectedTeacher.value) {
            sets.push(pinnedTeacherGroupIds.value);
        }
        return intersectionOf(sets);
    });

    const enabledTeacherIds = computed<ReadonlySet<string> | null>(() => {
        // Pinned program/group already scope the teacher LIST itself (loadTeachers).
        const sets: Array<ReadonlySet<string>> = [];
        if (pinnedSite.value && selectedSite.value) {
            const siteId = selectedSite.value.id;
            const programsAtSite = new Set(
                programs.value.filter(p => p.sites.some(s => s.id === siteId)).map(p => p.id)
            );
            sets.push(new Set(
                teachers.value
                    .filter(t => (t.learningPrograms ?? []).some(p => programsAtSite.has(p.id)))
                    .map(t => t.id)
            ));
        }
        if (pinnedGroup.value && selectedGroup.value) {
            sets.push(pinnedGroupTeacherIds.value);
        }
        return intersectionOf(sets);
    });

    function programMatchesSelectedSite(program: LearningProgram): boolean {
        if (isProgramScope(program)) return true;
        return !selectedSite.value || program.sites.some(s => s.id === selectedSite.value!.id);
    }

    function groupMatchesSelectedProgram(group: Group): boolean {
        return !selectedProgram.value || group.learningProgramId === selectedProgram.value.id;
    }

    function warnIfPinnedTeacherIsUnavailable(loadSucceeded: boolean): void {
        if (!loadSucceeded || !pinnedTeacher.value || !selectedTeacher.value) return;
        if (teachers.value.some(t => t.id === selectedTeacher.value!.id)) return;
        showWarning(translate('parcours.toolbar.warning.pinnedTeacherUnavailable'));
    }

    /** Persists selected IDs and pin states to localStorage. */
    function saveToStorage() {
        const state: SavedToolbarState = {
            siteId: selectedSite.value?.id ?? null,
            programId: selectedProgram.value?.id ?? null,
            confirmedGroupId: selectedGroupConfirmed.value?.id ?? null,
            unconfirmedGroupId: selectedGroupUnconfirmed.value?.id ?? null,
            allGroupId: selectedGroupAll.value?.id ?? null,
            teacherId: selectedTeacher.value?.id ?? null,
            pinnedSite: pinnedSite.value,
            pinnedProgram: pinnedProgram.value,
            pinnedGroup: pinnedGroup.value,
            pinnedTeacher: pinnedTeacher.value,
            studentFilter: normalizeStudentFilter(studentFilter.value),
            activityTypes: selectedActivityTypes.value,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }

    /**
     * Restores selections from localStorage after all data has been loaded.
     * Skips cascade resets while restoring so all selections are applied together.
     */
    async function restoreFromStorage() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const saved: SavedToolbarState = JSON.parse(raw);

            // initialize() loads the complete teacher list before applying the saved scope.
            // Keep the matching object so a pinned teacher can survive even when the scoped
            // response intentionally omits them.
            const globallyRestoredTeacher = saved.teacherId
                ? teachers.value.find(t => t.id === saved.teacherId) ?? null
                : null;

            isRestoring = true;

            if (saved.siteId) {
                const site = sites.value.find(s => s.id === saved.siteId);
                if (site) selectedSite.value = site;
            }
            if (saved.programId) {
                const candidates = saved.pinnedProgram ? programs.value : filteredPrograms.value;
                const program = candidates.find(p => p.id === saved.programId);
                if (program) selectedProgram.value = program;
            }
            const confirmedGroupId = saved.confirmedGroupId ?? saved.groupId;
            if (confirmedGroupId) {
                const group = groups.value
                    .filter(g => !g.archived && g.status === GroupStatus.Confirmed)
                    .find(g => g.id === confirmedGroupId);
                if (group) selectedGroupConfirmed.value = group;
            }
            if (saved.unconfirmedGroupId) {
                const group = groups.value
                    .filter(g => !g.archived && g.status === GroupStatus.Unconfirmed)
                    .find(g => g.id === saved.unconfirmedGroupId);
                if (group) selectedGroupUnconfirmed.value = group;
            }
            if (saved.allGroupId) {
                const group = groups.value
                    .filter(g => !g.archived)
                    .find(g => g.id === saved.allGroupId);
                if (group) selectedGroupAll.value = group;
            }
            pinnedSite.value = saved.pinnedSite ?? false;
            pinnedProgram.value = saved.pinnedProgram ?? false;
            pinnedGroup.value = saved.pinnedGroup ?? false;
            pinnedTeacher.value = saved.pinnedTeacher ?? false;
            studentFilter.value = saved.studentFilter
                ? { ...emptyStudentFilter(), ...saved.studentFilter }
                : emptyStudentFilter();
            // The catalogue may already be loaded when the restore runs (both start on the
            // same route change), so the pruning happens on whichever comes second.
            selectedActivityTypes.value = normalizeActivityTypes(saved.activityTypes);
            pruneActivityTypesToCatalogue();

            // Let queued watchers run while isRestoring is still true.
            await nextTick();
            isRestoring = false;

            if (saved.teacherId) {
                const loadSucceeded = await loadTeachers();
                const scopedTeacher = teachers.value.find(t => t.id === saved.teacherId) ?? null;
                const teacher = scopedTeacher
                    ?? (saved.pinnedTeacher ? globallyRestoredTeacher : null);
                if (teacher) selectedTeacher.value = teacher;
                warnIfPinnedTeacherIsUnavailable(loadSucceeded);
            }

            if (pinnedProgram.value && selectedProgram.value && !programMatchesSelectedSite(selectedProgram.value)) {
                showWarning(translate('parcours.toolbar.warning.pinnedProgramUnavailable'));
            }
            if (pinnedGroup.value && selectedGroup.value && !groupMatchesSelectedProgram(selectedGroup.value)) {
                showWarning(translate('parcours.toolbar.warning.pinnedGroupUnavailable'));
            }
        } catch {
            isRestoring = false;
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    /**
     * True when the signed-in user's own data is everything the teacher filter could ever
     * show: a scoped Enseignant (holding `Enseignant` with none of the unrestricted-scope
     * permissions) is bounded server-side to their own frequentations regardless of what this
     * dropdown says (#540, #879) — picking a colleague, or clearing it to "Tous", would
     * silently have no effect on what the page actually displays. The comment on #879 named
     * this directly: showing that choice as if it mattered is misleading. An « Enseignant
     * chef » keeps the live filter, like a Superviseur: the server lets them read a colleague,
     * within the programs assigned to them (#1083).
     */
    const teacherFilterLockedToSelf = computed(() => {
        const permissions = coreSdk.me.value?.permissions;
        if (!permissions?.includes(PERMISSION_ENSEIGNANT)) return false;
        return !unrestrictedTeacherScope.value;
    });

    /**
     * Defaults the teacher dropdown to the signed-in user when they are a teacher and no
     * teacher is already selected (e.g. after restore/deep-link). This only sets the initial
     * value — the user remains free to pick another teacher or clear the selection ("all").
     * Clearing it is intentionally not persisted: on reload the default re-applies.
     *
     * A scoped Enseignant (#879) gets more than a default: `teacherFilterLockedToSelf` forces
     * and pins the value to themselves unconditionally, overriding whatever was
     * selected/restored — see its own doc comment. The toolbar UI disables the unpin action
     * for this case, so this is the only place the pin is asserted.
     */
    function applyDefaultTeacherSelection() {
        const me = coreSdk.me.value;
        if (!me?.permissions?.includes(PERMISSION_ENSEIGNANT)) return;
        const own = teachers.value.find(t => t.user.id === me.user.id);

        if (teacherFilterLockedToSelf.value) {
            if (own) selectedTeacher.value = own;
            pinnedTeacher.value = true;
            return;
        }

        if (selectedTeacher.value) return;
        if (own) selectedTeacher.value = own;
    }

    /** Load all reference data needed by the toolbar. */
    async function initialize() {
        // Net under the bar, which draws no axis for the exam supervisor (#1164): every list here
        // answers them 403. Left uninitialized, so a later account on this device loads its own.
        if (examSupervisorOnly.value) return;
        await Promise.all([loadSites(), loadPrograms(), loadGroups()]);
        await loadTeachers();
        await restoreFromStorage();
        applyDefaultTeacherSelection();
        initialized.value = true;
        saveToStorage(); // Overwrite storage with the actually-valid restored state.
    }

    /**
     * Resolves once the bar has restored the saved selection — at once if it already has, or if it
     * never will (the exam supervisor, for whom `initialize` returns early). A page that loads
     * from the selection on mount waits for this: after a reload, its first read otherwise ran
     * before the saved program came back, and the program then arrived unseen (#1314).
     */
    function whenRestored(): Promise<void> {
        const ready = () => initialized.value || examSupervisorOnly.value;
        if (ready()) return Promise.resolve();
        return new Promise(resolve => {
            const stop = watch(ready, isReady => {
                if (!isReady) return;
                stop();
                resolve();
            });
        });
    }

    // ─── Cascading watchers ────────────────────────────────────────────────────

    // The permissions can settle after the programs did (a session still signing in, an
    // identity borrowed by an administrator): decide the pseudo-program's presence again, and
    // drop it from the selection once it is no longer offered.
    watch(unrestrictedTeacherScope, () => {
        programs.value = offerProgramScopes(programs.value);
        if (selectedProgram.value && isProgramScope(selectedProgram.value)
            && !programs.value.some(p => p.id === selectedProgram.value!.id)) {
            selectedProgram.value = null;
        }
    });

    watch(selectedSite, async () => {
        const skipCascade = isRestoring;
        // Reload programs with the correct scope (available-only vs all for a site).
        await loadPrograms();
        // Reset program if it no longer belongs to the newly selected site.
        if (!skipCascade && selectedSite.value && selectedProgram.value) {
            if (!programMatchesSelectedSite(selectedProgram.value) && !pinnedProgram.value) {
                selectedProgram.value = null;
            } else if (!programMatchesSelectedSite(selectedProgram.value)) {
                showWarning(translate('parcours.toolbar.warning.pinnedProgramUnavailable'));
            }
        }
    });

    watch(selectedProgram, async () => {
        const skipCascade = isRestoring;
        if (!skipCascade) {
            if (!pinnedGroup.value) {
                selectedGroupConfirmed.value = null;
                selectedGroupUnconfirmed.value = null;
                // `selectedGroupAll` was missing from this list (#819). `selectedGroup` reads one of
                // the three slots depending on `groupStatusFilter`: with the « all » filter, changing
                // program therefore left a group of the PREVIOUS program selected. That is one of the
                // ways to end up with an empty assiduity grid — program and group no longer refer to
                // the same set, so no membership matches.
                selectedGroupAll.value = null;
            } else if (selectedGroup.value && !groupMatchesSelectedProgram(selectedGroup.value)) {
                showWarning(translate('parcours.toolbar.warning.pinnedGroupUnavailable'));
            }
            if (!pinnedTeacher.value) selectedTeacher.value = null;
        }
        const loadSucceeded = await loadTeachers();
        if (!skipCascade) warnIfPinnedTeacherIsUnavailable(loadSucceeded);
    });

    watch(selectedGroup, async () => {
        const skipCascade = isRestoring;
        if (!skipCascade && !pinnedTeacher.value) {
            selectedTeacher.value = null;
        }
        const loadSucceeded = await loadTeachers();
        if (!skipCascade) warnIfPinnedTeacherIsUnavailable(loadSucceeded);
    });

    // Once an incompatible value is unpinned, return the corresponding dropdown to a valid
    // state instead of leaving it blank with an ID that is absent from its options.
    watch(pinnedProgram, (isPinned) => {
        if (!isPinned && selectedProgram.value && !programMatchesSelectedSite(selectedProgram.value)) {
            selectedProgram.value = null;
        }
    });

    watch(pinnedGroup, (isPinned) => {
        if (isPinned || !selectedProgram.value) return;
        if (selectedGroupConfirmed.value && !groupMatchesSelectedProgram(selectedGroupConfirmed.value)) {
            selectedGroupConfirmed.value = null;
        }
        if (selectedGroupUnconfirmed.value && !groupMatchesSelectedProgram(selectedGroupUnconfirmed.value)) {
            selectedGroupUnconfirmed.value = null;
        }
        if (selectedGroupAll.value && !groupMatchesSelectedProgram(selectedGroupAll.value)) {
            selectedGroupAll.value = null;
        }
    });

    watch(pinnedTeacher, (isPinned) => {
        // #879: the lock is a store-level invariant, not merely a disabled button — anything
        // that unpins a locked filter (a stale code path, a future caller) is reverted here,
        // not just the one button the toolbar disables.
        if (!isPinned && teacherFilterLockedToSelf.value) {
            pinnedTeacher.value = true;
            return;
        }
        if (!isPinned && selectedTeacher.value
            && !teachers.value.some(t => t.id === selectedTeacher.value!.id)) {
            selectedTeacher.value = null;
        }
    });

    // ─── Persist to localStorage on any selection or pin change ───────────────
    watch(
        [selectedSite, selectedProgram, selectedGroupConfirmed, selectedGroupUnconfirmed, selectedGroupAll, selectedTeacher, pinnedSite, pinnedProgram, pinnedGroup, pinnedTeacher, studentFilter, selectedActivityTypes],
        () => {
            if (!isRestoring && initialized.value) saveToStorage();
        }
    );
    /**
     * Switches the group status filter and restores the saved group selection for the new
     * filter without triggering the cascade watcher (teacher is preserved).
     */
    async function switchGroupStatusFilter(newFilter: GroupStatus | 'all') {
        if (newFilter === groupStatusFilter.value) return;
        isRestoring = true;
        groupStatusFilter.value = newFilter;
        await nextTick();
        isRestoring = false;
    }

    function setHighlight(items: ContextDropdown[], routeName: HighlightRouteName, owner: symbol) {
        highlight.value = items;
        highlightRoute.value = routeName;
        highlightOwner.value = owner;
    }

    function clearHighlight(owner: symbol) {
        if (highlightOwner.value !== owner) return;
        highlight.value = [];
        highlightRoute.value = null;
        highlightOwner.value = null;
    }

    function setAxisRestriction(axes: ContextDropdown[], routeName: HighlightRouteName, owner: symbol) {
        axisRestriction.value = axes;
        axisRestrictionRoute.value = routeName;
        axisRestrictionOwner.value = owner;
    }

    function clearAxisRestriction(owner: symbol) {
        if (axisRestrictionOwner.value !== owner) return;
        axisRestriction.value = null;
        axisRestrictionRoute.value = null;
        axisRestrictionOwner.value = null;
    }

    // ─── Public API ────────────────────────────────────────────────────────────
    return {
        // Lists
        sites,
        programs,
        groups,
        teachers,
        filteredPrograms,
        filteredGroups,
        filteredTeachers,
        // Loading
        loadingSites,
        loadingPrograms,
        loadingGroups,
        loadingTeachers,
        // Selection
        selectedSite,
        selectedProgram,
        selectedGroup,
        selectedTeacher,
        // Pin state
        pinnedSite,
        pinnedProgram,
        pinnedGroup,
        pinnedTeacher,
        teacherFilterLockedToSelf,
        unrestrictedTeacherScope,
        examSupervisorOnly,
        whenRestored,
        pinnedTeacherGroupIds,
        pinnedTeacherProgramIds,
        pinnedGroupTeacherIds,
        // Pin constraints for the menus (null = unconstrained)
        enabledSiteIds,
        enabledProgramIds,
        enabledGroupIds,
        enabledTeacherIds,
        // Group status filter
        groupStatusFilter,
        // Student filter criteria
        studentFilter,
        // Activity filter (#1163)
        selectedActivityTypes,
        selectedActivityLabel,
        activityTypes,
        loadingActivityTypes,
        setActivityTypes,
        loadActivityTypes,
        // Highlight
        highlight,
        highlightRoute,
        setHighlight,
        clearHighlight,
        // Axis restriction (#924)
        axisRestriction,
        axisRestrictionRoute,
        setAxisRestriction,
        clearAxisRestriction,
        // Initialization
        initialized,
        // Actions
        loadSites,
        loadPrograms,
        loadGroups,
        loadTeachers,
        switchGroupStatusFilter,
        initialize,
        // Lookup helpers
        findSite,
        findProgram,
        findGroup,
        findTeacher,
    };
});
